import {
  createContext,
  createEffect,
  JSXElement,
  mergeProps,
  Show,
  untrack,
  useContext
} from 'solid-js';
import { ConnectionData } from '~/api/types/connectionData.ts';
import { ConnectedAppProvider } from '~/api/types/connectedAppProviders.ts';
import { createStore } from 'solid-js/store';
import { useNavigate, useParams } from '@solidjs/router';
import {
  getConnectedAppProviderByConnectionIdQueryKey,
  getConnectedAppProviders,
  getConnectionDetailsQuery,
  getEnvironmentConnectionDetails,
  isApiError,
  retryAuthorizedRequests
} from '~/api';
import { useQuery } from '@tanstack/solid-query';
import { getConnectedAppProviderDetails } from '~/pages/ScalekitConnect/commons/utils.ts';
import {
  navigateToConnectionList,
  PROVIDER_LIST_PAGE_SIZE
} from '~/pages/ScalekitConnect/Integrations/utils.tsx';
import SkLoader from '~/components/ui/SkLoader';
import { cloneObject } from '~/utils/commonUtils.ts';
import { HttpStatusCode } from 'axios';
import { useAppData } from '~/components/AppData';

export type ConnectedAppContextValueType = {
  connectedApp: ConnectionData | null;
  connectedAppProvider: ConnectedAppProvider | null;
  refetchConnectedApp: () => Promise<void>;
  shouldShowAddConnectedAccount: boolean;
  showAddConnectedAccount: () => void;
  hideAddConnectedAccount: () => void;
  setConnectedApp: (connectedApp: ConnectionData | null) => void;
  setConnectedAppProvider: (provider: ConnectedAppProvider) => void;
};

const defaultContextValue: ConnectedAppContextValueType = {
  connectedApp: null,
  connectedAppProvider: null,
  refetchConnectedApp: async () => {},
  shouldShowAddConnectedAccount: false,
  showAddConnectedAccount: () => {},
  hideAddConnectedAccount: () => {},
  setConnectedApp: (_) => {},
  setConnectedAppProvider: (_) => {}
};

const ConnectedAppContext =
  createContext<ConnectedAppContextValueType>(defaultContextValue);

type ConnectedAppContextProviderPropType = {
  children: JSXElement;
  connectedApp?: ConnectionData;
  connectedAppProvider?: ConnectedAppProvider;
};

export default function ConnectedAppContextProvider(
  props: ConnectedAppContextProviderPropType
): JSXElement {
  const merged = mergeProps({}, props);
  const params = useParams();
  const navigate = useNavigate();
  const appData = useAppData();

  const [contextValue, setContextValue] =
    createStore<ConnectedAppContextValueType>({
      ...defaultContextValue,
      connectedApp: merged.connectedApp ?? null,
      connectedAppProvider: merged.connectedAppProvider ?? null,
      refetchConnectedApp: async () => {
        await connectionDetailsQuery.refetch();
      },
      showAddConnectedAccount: () => {
        setContextValue('shouldShowAddConnectedAccount', true);
      },
      hideAddConnectedAccount: () => {
        setContextValue('shouldShowAddConnectedAccount', false);
      },
      setConnectedApp: (connectedApp) => {
        setContextValue('connectedApp', cloneObject(connectedApp));
        setIsDataLoaded('isConnectionDetailsLoaded', true);
      },
      setConnectedAppProvider: (provider) => {
        setContextValue('connectedAppProvider', provider);
        setIsDataLoaded('isProviderLoaded', true);
      }
    });
  const [isDataLoaded, setIsDataLoaded] = createStore<{
    isConnectionDetailsLoaded: boolean;
    isProviderLoaded: boolean;
  }>({
    isConnectionDetailsLoaded: !params.connectionId,
    isProviderLoaded: !params.connectionId
  });

  const connectionId = params.connectionId;

  const connectionDetailsQuery = useQuery(() => ({
    queryKey: getConnectionDetailsQuery(params.connectionId),
    queryFn: async () => {
      if (params.connectionId) {
        return await getEnvironmentConnectionDetails(params.connectionId);
      }
      return null;
    },
    retry: retryAuthorizedRequests,
    enabled: false
  }));

  if (connectionId && !contextValue.connectedApp) {
    connectionDetailsQuery.refetch();
  }

  createEffect(() => {
    if (connectionDetailsQuery.isSuccess && connectionDetailsQuery.data) {
      untrack(() => {
        setContextValue('connectedApp', connectionDetailsQuery.data);
        setIsDataLoaded('isConnectionDetailsLoaded', true);
      });
    } else if (
      connectionDetailsQuery.isError &&
      isApiError(connectionDetailsQuery.error) &&
      connectionDetailsQuery.error.httpStatus === HttpStatusCode.NotFound
    ) {
      navigateToConnectionList({
        navigate,
        environmentId: appData.selectedEnvironment!.id
      });
    }
  });

  const providerDetailsQuery = useQuery(() => ({
    queryKey: getConnectedAppProviderByConnectionIdQueryKey(connectionId),
    queryFn: async () =>
      await getConnectedAppProviders({
        pageSize: PROVIDER_LIST_PAGE_SIZE,
        identifier: connectionDetailsQuery.data?.providerKey
      }),
    retry: retryAuthorizedRequests,
    enabled: false
  }));

  createEffect(() => {
    if (
      contextValue.connectedApp?.id &&
      contextValue.connectedApp.provider !==
        contextValue.connectedAppProvider?.identifier
    ) {
      providerDetailsQuery.refetch();
    }
  });

  createEffect(() => {
    if (providerDetailsQuery.isSuccess && providerDetailsQuery.data) {
      untrack(() => {
        contextValue.connectedApp &&
          setContextValue(
            'connectedAppProvider',
            getConnectedAppProviderDetails(
              providerDetailsQuery.data.providers ?? [],
              contextValue.connectedApp?.providerKey ?? ''
            )
          );
        setIsDataLoaded('isProviderLoaded', true);
      });
    }
  });

  return (
    <ConnectedAppContext.Provider value={contextValue}>
      <Show
        when={
          isDataLoaded.isConnectionDetailsLoaded &&
          isDataLoaded.isProviderLoaded
        }
        fallback={<SkLoader />}
      >
        {merged.children}
      </Show>
    </ConnectedAppContext.Provider>
  );
}

export const useConnectedAppContext = () =>
  useContext(ConnectedAppContext) as ConnectedAppContextValueType;
